import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/* PULSANTE AGGIUNGI AL CARRELLO PRODUCT PAGE*/
(function() {
  const style = document.createElement('style');
  style.textContent = `
    .add-to-cart-button {
      background: #000000 !important;
      border: none !important;
      border-radius: 0px !important;
      font-weight: 700 !important;
      font-size: 13px !important;
      letter-spacing: 0.15em !important;
      text-transform: uppercase !important;
      color: #ffffff !important;
      cursor: pointer !important;
      position: relative !important;
      overflow: hidden !important;
    }

    .add-to-cart-button .add-to-cart-icon {
      display: none !important;
    }

    .atc-shimmer-overlay {
      position: absolute !important;
      inset: 0 !important;
      display: block !important;
      pointer-events: none !important;
      z-index: 2 !important;
      overflow: hidden !important;
    }

    .atc-shimmer-overlay::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 45% !important;
      height: 100% !important;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 120, 0, 0.35) 40%,
        rgba(255, 165, 0, 0.55) 50%,
        rgba(255, 120, 0, 0.35) 60%,
        transparent 100%
      ) !important;
      animation: atc-shimmer-move 2s ease-in-out infinite !important;
    }

    .add-to-cart-button.filling::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 0% !important;
      height: 100% !important;
      background: linear-gradient(
        90deg,
        rgba(255, 100, 0, 0.85) 0%,
        rgba(255, 160, 0, 0.95) 100%
      ) !important;
      pointer-events: none !important;
      z-index: 1 !important;
      animation: fill-orange 0.6s ease-out forwards !important;
    }

    @keyframes fill-orange {
      0%   { width: 0%; }
      100% { width: 100%; }
    }

    @keyframes atc-shimmer-move {
      0%   { transform: translateX(-120%); }
      100% { transform: translateX(280%); }
    }
  `;
  document.head.appendChild(style);

  function ensureShimmer(btn) {
    btn.style.setProperty('position', 'relative', 'important');
    btn.style.setProperty('overflow', 'hidden', 'important');

    if (!btn.querySelector(':scope > .atc-shimmer-overlay')) {
      const shimmer = document.createElement('span');
      shimmer.className = 'atc-shimmer-overlay';
      shimmer.setAttribute('aria-hidden', 'true');
      btn.appendChild(shimmer);
    }

    if (btn.dataset.behaviorApplied) return;
    btn.dataset.behaviorApplied = 'true';
    btn.addEventListener('click', function() {
      btn.classList.add('filling');
      setTimeout(() => btn.classList.remove('filling'), 700);
    });
  }

  function applyCartButtonBehavior() {
    document.querySelectorAll('.add-to-cart-button').forEach(ensureShimmer);
  }

  document.addEventListener('DOMContentLoaded', () => setTimeout(applyCartButtonBehavior, 300));
  document.addEventListener('variant:update', () => setTimeout(applyCartButtonBehavior, 50));
  document.addEventListener('variant:selected', () => setTimeout(applyCartButtonBehavior, 50));
  const observer = new MutationObserver(applyCartButtonBehavior);
  observer.observe(document.body, { childList: true, subtree: true });
  setInterval(applyCartButtonBehavior, 500);
})();


/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

// Badge "In Stock" stabile taglia selezionata
// Inietta stili direttamente nel DOM — nessuna dipendenza da file CSS esterni
(function() {
  const style = document.createElement('style');
  style.textContent = `
    .stock-badge {
      display: inline-flex !important;
      align-items: center !important;
      gap: 5px !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.08em !important;
      margin-left: 0 !important;
    }
    legend > .stock-badge {
      display: none !important;
    }
    .pdp-stock-slot .stock-badge {
      display: inline-flex !important;
    }
    .stock-badge--hidden {
      display: none !important;
    }
    /* IN STOCK — verde */
    .stock-badge--available {
      color: #1a7a2e !important;
    }
    .stock-badge--available::after {
      content: '' !important;
      display: inline-block !important;
      width: 7px !important;
      height: 7px !important;
      border-radius: 50% !important;
      background: #1a7a2e !important;
      animation: pulse-green 2s infinite !important;
      flex-shrink: 0 !important;
    }
    /* SOLD OUT — rosso */
    .stock-badge--soldout {
      color: #b71c1c !important;
    }
    .stock-badge--soldout::after {
      content: '' !important;
      display: inline-block !important;
      width: 7px !important;
      height: 7px !important;
      border-radius: 50% !important;
      background: #b71c1c !important;
      animation: pulse-red 2s infinite !important;
      flex-shrink: 0 !important;
    }
    @keyframes pulse-green {
      0%   { box-shadow: 0 0 0 0 rgba(26, 122, 46, 0.6); }
      70%  { box-shadow: 0 0 0 7px rgba(26, 122, 46, 0); }
      100% { box-shadow: 0 0 0 0 rgba(26, 122, 46, 0); }
    }
    @keyframes pulse-red {
      0%   { box-shadow: 0 0 0 0 rgba(183, 28, 28, 0.6); }
      70%  { box-shadow: 0 0 0 7px rgba(183, 28, 28, 0); }
      100% { box-shadow: 0 0 0 0 rgba(183, 28, 28, 0); }
    }
  `;
  document.head.appendChild(style);
})();

function updateStockBadge() {
  document.querySelectorAll('legend > .stock-badge').forEach((badge) => badge.remove());

  document.querySelectorAll('.pdp-stock-slot').forEach((slot) => {
    const fieldset = slot.closest('.variant-option');
    if (!fieldset) return;
    const checkedInput = fieldset.querySelector('input:checked');
    let badge = slot.querySelector('.stock-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'stock-badge stock-badge--hidden';
      slot.appendChild(badge);
    }
    if (checkedInput) {
      const available = checkedInput.dataset.optionAvailable === 'true';
      badge.classList.remove('stock-badge--hidden', 'stock-badge--available', 'stock-badge--soldout');
      if (available) {
        badge.textContent = 'In Stock';
        badge.classList.add('stock-badge--available');
      } else {
        badge.textContent = 'Sold Out';
        badge.classList.add('stock-badge--soldout');
      }
    } else {
      badge.classList.add('stock-badge--hidden');
    }
  });
}

document.addEventListener('DOMContentLoaded', () => setTimeout(updateStockBadge, 500));

document.addEventListener('click', function(e) {
  if (e.target.closest('.variant-option__button-label')) {
    setTimeout(updateStockBadge, 100);
    setTimeout(updateStockBadge, 400);
    setTimeout(updateStockBadge, 800);
  }
});

setInterval(updateStockBadge, 600);

/*IN PROMO BADGE RODUCT PAGE*/
(function() {
  // Aggiungi al style già esistente oppure crea un nuovo tag style
  const style = document.createElement('style');
  style.textContent = `
    .promo-badge {
      display: inline-flex !important;
      align-items: center !important;
      gap: 4px !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.1em !important;
      margin-left: 10px !important;
      color: #FF8D2B !important;
      animation: glow-orange 2s ease-in-out infinite !important;
    }

    @keyframes glow-orange {
      0%, 100% { text-shadow: 0 0 4px rgba(255, 106, 0, 0.4), 0 0 8px rgba(255, 106, 0, 0.2); }
      50%       { text-shadow: 0 0 8px rgba(255, 106, 0, 1), 0 0 18px rgba(255, 106, 0, 0.6), 0 0 30px rgba(255, 106, 0, 0.3); }
    }
  `;
  document.head.appendChild(style);

  function updatePromoBadge() {
    document.querySelectorAll('.price__sale').forEach(priceBlock => {
      if (priceBlock.querySelector('.promo-badge')) return;

      // Prendi il prezzo barrato (compare-at-price) invece del prezzo scontato
      const comparePrice = priceBlock.querySelector('.compare-at-price');
      if (!comparePrice) return;

      const badge = document.createElement('span');
      badge.className = 'promo-badge';
      badge.textContent = '⚡ In Promo';
      comparePrice.insertAdjacentElement('afterend', badge);
    });
  }

  document.addEventListener('DOMContentLoaded', () => setTimeout(updatePromoBadge, 500));
  setInterval(updatePromoBadge, 800);
})();

